import * as React from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  id?: string
  eyebrow?: string
  title?: string
  description?: string
}

/**
 * Shared section shell — consistent vertical rhythm and heading treatment
 * across every landing block. The heading eases in the first time it scrolls
 * into view.
 */
export function Section({
  id,
  eyebrow,
  title,
  description,
  className,
  children,
  ...props
}: SectionProps) {
  const reduceMotion = useReducedMotion()

  return (
    <section id={id} className={cn('mx-auto max-w-6xl px-4 py-20', className)} {...props}>
      {(eyebrow || title || description) && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto mb-12 max-w-2xl text-center"
        >
          {eyebrow && (
            <p className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
              <span
                aria-hidden
                className="h-px w-6 bg-linear-to-r from-transparent to-blue-600 dark:to-blue-300"
              />
              {eyebrow}
              <span
                aria-hidden
                className="h-px w-6 bg-linear-to-l from-transparent to-blue-600 dark:to-blue-300"
              />
            </p>
          )}
          {title && (
            <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {title}
            </h2>
          )}
          {description && (
            <p className="mt-4 text-pretty text-base leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
        </motion.div>
      )}
      {children}
    </section>
  )
}
